/**
 * Renderer-side audio capture and WebSocket transcription service.
 *
 * Runs in the overlay renderer (Electron) or directly in a browser.
 * Captures mic and system audio via Web APIs, each with its own
 * WebSocket connection to the backend AudioSession DO endpoint.
 * Updates GlobalState directly — no PCM data crosses IPC boundaries.
 *
 * Mic connection:    isHost=true,  diarization on (default single speaker,
 *                    multi-speaker detected when multiple DG indices appear)
 * System connection: isHost=false, diarization on (multi speaker)
 *
 * This file is the orchestrator: it owns the public API the rest of
 * the renderer talks to (start/stop/pause/resume/forceSegment/etc.),
 * the cross-channel state (text segments, speaker resolver,
 * pendingForceSegments), and the lifecycle of the underlying
 * AudioChannel instances. Per-channel audio capture, PCM processing,
 * WebSocket transport, and diagnostics live in the
 * `audio-transcription/` sub-modules.
 */
import { GlobalEvents, GlobalState, createBroadcastProducer } from '@highlight/ipc'
import * as Sentry from '@sentry/electron/renderer'
import { getChatApiUrl } from '@shared/utils/ChatBackendUtils'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import { SpeakerNameResolver } from './speaker-name-resolver'
import {
  AudioCaptureSource,
  type DebugCaptureStage,
  MicCaptureSource,
  SystemCaptureSource,
  snapshotAudioOutputs,
} from './audio-transcription/audio-capture-source'
import { clearAudioDebugChannelState, publishAudioDebugEvent } from './audio-transcription/audio-debug'
import { AudioChannel, type AudioChannelDelegate, type AudioChannelOwner } from './audio-transcription/audio-channel'
import type { AudioChannelDebugInfo, ElectronAudioRuntimeDebugInfo } from '@shared/types/audio-debug-info'
import { AUDIO_LEVELS_TOPIC, EMPTY_AUDIO_LEVELS, type AudioLevelsSnapshot } from '@shared/audio-levels'
import { ChannelDiagnosticsReporter } from './audio-transcription/channel-diagnostics'
import { publishPartialTranscription } from './audio-transcription/partials-publisher'
import { reportSegmentEndToMain } from './audio-transcription/segment-end'
import { reportMicKindToMain } from './audio-transcription/mic-kind-report'
import { micKindForLabel } from '@shared/mic-kind'
import { TranscriptDispatcher } from './audio-transcription/transcript-dispatcher'
import { scoreAgainstFingerprint } from './self-voice/fingerprint-score'
import { SelfVoiceSession } from './self-voice/self-voice-session'
import { loadVoiceEmbedder } from './self-voice/voice-embedder'
import {
  SELF_VOICE_FIRST_MATCH_EVENT,
  SELF_VOICE_SESSION_CONFIG_EVENT,
  SELF_VOICE_SESSION_END_EVENT,
  USER_VOICE_MATCHES_STATE,
  type MicEpochVerdict,
  type SelfVoiceSessionConfig,
} from '@shared/self-voice'
import type { UserVoiceMatches } from '@shared/utils/audio-notes/mic-label'
import {
  ENERGY_REPORT_INTERVAL_MS,
  FORCE_SEGMENT_TIMEOUT_MS,
  SOURCE_BOTH,
  SOURCE_LOOPBACK,
  SOURCE_MICROPHONE,
  TARGET_BYTES_PER_SECOND,
  micDeviceKeyFor,
  wireChannelName,
  type CaptureErrorPayload,
  type ChannelLabel,
  type ServiceStatus,
} from './audio-transcription/types'
import {
  MEETING_MIC_GATE_KEY,
  meetingMicGatesEqual,
  NEUTRAL_MEETING_MIC_GATE,
  normalizeMeetingMicGate,
  type MeetingMicGate,
} from '@shared/types/meeting-mic-gate'

/**
 * Cap on how many times a single channel's unexpected track ending fires a
 * Sentry event per session. We capture more than one so volume tells us
 * "track ends once then recovers" vs "track ends in a tight loop hundreds of
 * times" — those are very different bugs. Past this cap, further endings still
 * trigger recovery but stop firing Sentry events so a wedged session doesn't
 * flood the project.
 */
const TRACK_ENDED_CAPTURE_LIMIT = 3

/** Human-readable one-liner for the meeting mic-gate transition log. */
function describeMicGate(gate: MeetingMicGate): string {
  if (gate.shouldDropMic) return `MUTED (mic dropping${gate.mergedAudio ? ', merged' : ''})`
  if (gate.selfMute === true) return `muted but mic capturing (user opt-in${gate.mergedAudio ? ', merged' : ''})`
  return 'UNMUTED (mic capturing)'
}

/** Shape published on `audio-transcription-service.buffered`. Whole
 *  seconds of PCM held in each channel's disconnect / pre-server-ready
 *  buffer. Both fields are `0` when there's no live channel or the
 *  service is idle. Renderer consumers can show a "Reconnecting · Xs
 *  buffered" pill when either value is > 0. */
interface BufferedSecondsState {
  mic: number
  sys: number
}

/** Both-channels-zero, used to skip publishes when there's no change. */
const EMPTY_BUFFERED_STATE: BufferedSecondsState = { mic: 0, sys: 0 }

interface PendingForceSegment {
  resolve: () => void
  timeoutHandle: ReturnType<typeof setTimeout>
}

/** Build the per-channel slice of the debug-info snapshot. Lives at module
 *  scope (not on the class) so the empty-channel case never has to read
 *  service state — easier to reason about, no chance of leaking partial
 *  internals when `ch === null`. */
function snapshotChannel(ch: AudioChannel | null): AudioChannelDebugInfo {
  if (!ch) {
    return {
      present: false,
      hasLiveTrack: false,
      wsConnected: false,
      wsReadyState: null,
      reconnectAttempts: 0,
      reconnectDelayMs: 0,
      serverReady: false,
      pcmBufferBytes: 0,
      disconnectedAt: null,
      speakerIndexOffset: 0,
      maxSpeakerIndexSeen: -1,
      wsSessionStartedAt: 0,
    }
  }
  const d = ch.inspectDebug()
  return {
    present: true,
    hasLiveTrack: d.hasLiveTrack,
    wsConnected: ch.isWsOpen(),
    wsReadyState: d.wsReadyState,
    reconnectAttempts: d.reconnectAttempts,
    reconnectDelayMs: d.reconnectDelayMs,
    serverReady: d.serverReady,
    pcmBufferBytes: d.pcmBufferBytes,
    disconnectedAt: d.disconnectedAt,
    speakerIndexOffset: d.speakerIndexOffset,
    maxSpeakerIndexSeen: d.maxSpeakerIndexSeen,
    wsSessionStartedAt: d.wsSessionStartedAt,
  }
}

/**
 * Hot-path broadcast topic for audio levels. Lazily registered the first
 * time the energy reporter starts, so the overlay's idle copy of this
 * service singleton (which never produces) doesn't allocate ghost
 * producer ports in the broker.
 *
 * Levels are published every {@link ENERGY_REPORT_INTERVAL_MS} (~10 Hz)
 * by the audio-runtime hidden window. Going through the broadcast broker
 * avoids waking the main JS loop on every tick.
 */
type LevelsProducer = ReturnType<typeof createBroadcastProducer<AudioLevelsSnapshot>>
let audioLevelsProducer: LevelsProducer | null = null
function ensureAudioLevelsProducer(): LevelsProducer {
  if (!audioLevelsProducer) {
    audioLevelsProducer = createBroadcastProducer<AudioLevelsSnapshot>(AUDIO_LEVELS_TOPIC)
  }
  return audioLevelsProducer
}

function isPermissionDeniedError(error: unknown): boolean {
  const name = error instanceof Error ? error.name : ''
  const message = error instanceof Error ? error.message : String(error)
  return name === 'NotAllowedError' || name === 'SecurityError' || /permission denied|not allowed/i.test(message)
}

/** Resolve after `ms`, or early (still resolves) when `signal` aborts. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve()
      },
      { once: true },
    )
  })
}

class AudioTranscriptionService {
  private status: ServiceStatus = 'idle'
  private micChannel: AudioChannel | null = null
  private systemChannel: AudioChannel | null = null

  private energyTimer: ReturnType<typeof setInterval> | null = null
  private readonly diagnosticsReporter = new ChannelDiagnosticsReporter()
  private lastMicEnergy = 0
  private lastSystemEnergy = 0
  /** Whole-second buffered PCM duration last published to GlobalState,
   *  per channel. Used by the energy-tick publisher to suppress no-op
   *  emits — buffered bytes change every PCM frame (~20ms) but the
   *  rounded second only flips ~1Hz, so we only publish on an actual
   *  flip. */
  private lastBufferedPublished: BufferedSecondsState = { ...EMPTY_BUFFERED_STATE }
  /** Latest published levels — served to main-process pullers via the
   *  `audio-runtime:get-levels` IPC handler. */
  private latestPublishedLevels: AudioLevelsSnapshot = EMPTY_AUDIO_LEVELS
  private locale = 'en-US'
  private micDeviceId: string | undefined
  /** DeviceId the *active* mic channel was last opened on (set after a
   *  successful `startMicChannel`). `switchMicDevice` uses this to detect
   *  whether an in-flight restart already picked up the requested device
   *  via `this.micDeviceId` and skip a redundant teardown / re-acquire. */
  private activeMicDeviceId: string | undefined = undefined
  /** Whether the caller asked for system-audio capture on this start().
   *  Lets the system capture-ended handler know whether to attempt a
   *  restart. */
  private systemCaptureRequested = false
  /** In-flight `stop()` promise, used so a concurrent `start()` can
   *  await teardown to complete instead of racing past the status
   *  flip and standing up channels that the still-running stop()
   *  is about to dispose. The IPC layer's `serviceRunning` flag
   *  serialises the obvious case, but defending in the service
   *  keeps any direct caller (tests, future renderers, alternate
   *  IPC paths) safe by construction. */
  private stopInFlight: Promise<void> | null = null
  /** In-flight system capture restart, used to deduplicate concurrent
   *  retries (e.g. multiple onended-style signals firing in quick
   *  succession). */
  private systemRestartInFlight: Promise<void> | null = null
  /** Abort handle for the background initial-system retry loop. Distinct from
   *  systemRestartInFlight because no track ever existed to fire onended;
   *  runStop aborts it so teardown never waits out a retry delay. */
  private initialSystemRetryAbort: AbortController | null = null
  /** In-flight mic restart, used to deduplicate concurrent retries
   *  (e.g. an unexpected mic onended firing during a pending
   *  switchMicDevice). */
  private micRestartInFlight: Promise<void> | null = null
  private sessionStartTime = 0
  private readonly speakerNameResolver = new SpeakerNameResolver()
  private audioSessionId: string | null = null
  /** True when the current session is a meeting recording that must be bound
   *  to its audio-session DO. Guards `start()`/`buildWsUrl()` against the
   *  stateless dictation `/transcribe-ws` fallback, which is unmetered and
   *  must only carry actual dictation (or local-storage recording) audio. */
  private audioSessionRequired = false
  /** Pending forceSegment requests awaiting their final transcript,
   *  keyed by source. */
  private pendingForceSegments: Map<number, Array<PendingForceSegment>> = new Map()
  /** The meeting's self-voice matching, while the `self-voice-match` flag is on. */
  private selfVoice: SelfVoiceSession | null = null
  private readonly transcripts = new TranscriptDispatcher(() => this.speakerNameResolver, {
    onForceSegmentResolved: (source) => this.resolvePendingForceSegments(source),
  })
  /** Stable fingerprint of the `audiooutput` device list at last
   *  successful system capture. Used by handleAudioOutputDeviceChange()
   *  to detect when macOS swaps the active audio output mid-session.
   *
   *  Why this matters: Chromium's CATAP loopback tap is bound to the
   *  default output endpoint at construction time. When that endpoint
   *  disappears (e.g. AirPods disconnect, USB DAC unplugged, BT
   *  speaker powered off) macOS reroutes the default to the
   *  next-priority device, but the tap stays bound to the now-defunct
   *  endpoint and produces digital silence forever. `track.onended`
   *  does NOT fire for this case — the track stays "live" with no
   *  samples. Only re-creating the displayMedia stream rebinds the
   *  tap to the current default. `null` means "no baseline yet" and
   *  suppresses the first restart until we've snapshotted. */
  private lastAudioOutputSnapshot: string | null = null
  /**
   * Active meeting mic-drop gate, mirrored reactively from the single
   * {@link MEETING_MIC_GATE_KEY} GlobalState value the main-process meeting AX
   * poller publishes. Carries `selfMute` + `mergedAudio` + the precomputed
   * `shouldDropMic` decision as one atomic object, so the capture hot path
   * reads a single boolean and never has to combine two independently-arriving
   * signals (which is what used to risk a brief mic drop on a merged+muted Meet
   * call).
   *
   * When `shouldDropMic` is true, the mic capture loop zeroes PCM frames before
   * they reach the WebSocket buffer so transcription doesn't fire on the user's
   * keyboard / breath / room noise while they're muted on the call. The SYSTEM
   * (loopback) channel is unaffected — far-end audio keeps streaming.
   *
   * Defaults to the neutral gate (do not drop) so a missing or transient AX
   * read can't silently kill capture.
   */
  private micGate: MeetingMicGate = NEUTRAL_MEETING_MIC_GATE
  private micGateListener: ((value: unknown) => void) | null = null

  /**
   * Per-channel `captureError` tracking. The published `CaptureErrorPayload`
   * has a single `channel` field for the toast title, but the underlying
   * source of truth is per-channel so a failure on one channel never clobbers
   * a still-active error on the other. The previous code (a single
   * `setCaptureError` call per restart path) had exactly this clobber bug —
   * the same one commit 743a42f fixed for `start()` but didn't propagate to
   * the restart / retry paths.
   *
   * Set via `setMicCaptureError` / `setSystemCaptureError` / `clearCaptureErrors`,
   * which automatically re-publish the merged payload through `publishCaptureError`.
   */
  private micCaptureErrorMessage: string | null = null
  private systemCaptureErrorMessage: string | null = null

  private readonly channelOwner: AudioChannelOwner = {
    getAuthToken: () => AuthIpcRenderer.getAccessToken(),
    buildWsUrl: (channel) => this.buildWsUrl(channel),
    isReconnectAllowed: () => this.status !== 'idle' && this.status !== 'stopping',
    shouldDropMicFrames: () => this.micGate.shouldDropMic,
  }

  private readonly channelDelegate: AudioChannelDelegate = {
    onPartial: (source, text, timestamp) => {
      // `timestamp` is the segment-start anchor (same one finals use for
      // startTime), so it maps directly onto the normalized contract.
      const partial = { text, startTimestamp: timestamp }
      if (source === SOURCE_MICROPHONE) publishPartialTranscription('microphone', partial)
      else if (source === SOURCE_LOOPBACK) publishPartialTranscription('loopback', partial)
    },
    onTranscript: (seg) => {
      this.transcripts.handleTranscript(seg)
      this.selfVoice?.addTranscript(seg)
    },
    onPcm: (source, pcm) => {
      if (source === SOURCE_MICROPHONE) this.selfVoice?.addMicAudio(pcm, Date.now())
    },
    onSegmentEnd: (source, ts) => {
      if (source === SOURCE_MICROPHONE || source === SOURCE_LOOPBACK) {
        reportSegmentEndToMain(source, ts)
      }
    },
    onEnergyUpdate: (source, rms) => {
      if (source === SOURCE_MICROPHONE) this.lastMicEnergy = rms
      else if (source === SOURCE_LOOPBACK) this.lastSystemEnergy = rms
    },
    onConnectionOpen: () => {
      // If we were in 'reconnecting' status because all channels were
      // disconnected, flip back to 'running' now that one is open.
      if (this.status === 'reconnecting') {
        this.setStatus('running')
        // Defensive liveness probe across the reconnecting → running
        // transition: a track that died during the dead window — pipeline
        // throw, OS yanking a USB mic mid-outage, AirPods sleeping — may
        // have been skipped by `onCaptureEnded`'s status guard. Without
        // this, a WS reconnect could leave the user "running" with a
        // dead capture and no meter activity until manual stop/start.
        if (this.micChannel && !this.micChannel.hasLiveTrack()) {
          console.warn('[AudioTranscriptionService] Reconnect-to-running with dead mic channel — restarting capture')
          this.lastMicEnergy = 0
          void this.restartMicCapture()
        }
        if (this.systemCaptureRequested && this.systemChannel && !this.systemChannel.hasLiveTrack()) {
          console.warn('[AudioTranscriptionService] Reconnect-to-running with dead system channel — restarting capture')
          this.lastSystemEnergy = 0
          void this.restartSystemCapture()
        }
      }
    },
    onConnectionDisconnect: () => {
      // If every live channel is now disconnected, flip status to
      // 'reconnecting'. Done here (not in the channel) because it's a
      // cross-channel decision.
      if ((this.status === 'running' || this.status === 'reconnecting') && this.allChannelsDisconnected()) {
        this.setStatus('reconnecting')
      }
    },
    onGapMarker: (channel, gapSeconds) => {
      this.transcripts.insertGapMarker(channel, gapSeconds)
    },
    onSpeakerNamespaceBumped: (source, offset) => {
      this.speakerNameResolver.notifyNamespaceBumped(source, offset)
    },
    onCaptureEnded: (channel, reason) => {
      if (reason === 'released') return
      if (channel === 'mic') {
        // Restart mic during 'running' AND 'reconnecting' — a track that
        // dies while the WS is also down (e.g. AirPods disconnect that
        // takes both with it) shouldn't be skipped just because we're
        // mid-reconnect. Do NOT restart while paused: reopening would
        // silently un-pause the mic on the user.
        if (this.status !== 'running' && this.status !== 'reconnecting') return
        console.warn('[AudioTranscriptionService] Mic track ended unexpectedly — attempting recovery')
        // Skip 'read-error': AudioChannel.handleCaptureError already reported it
        // to Sentry as `audio-capture-readloop-error`. Re-capturing here would
        // double up and wrongly advance the per-session track-ended counter for
        // a failure that isn't an OS track teardown.
        if (reason !== 'read-error') this.captureTrackEnded('mic', this.micChannel?.getPrimaryTrack())
        void this.restartMicCapture()
        return
      }
      // system loopback
      if (
        (this.status === 'running' || this.status === 'reconnecting' || this.status === 'paused') &&
        this.systemCaptureRequested
      ) {
        console.warn('[AudioTranscriptionService] System audio track ended unexpectedly — attempting recovery')
        // Skip 'read-error' — see the mic branch above.
        if (reason !== 'read-error') this.captureTrackEnded('system', this.systemChannel?.getPrimaryTrack())
        // Zero out RMS immediately so the meeting-end veto doesn't see
        // a stale loud value while the restart is in flight.
        this.lastSystemEnergy = 0
        void this.restartSystemCapture()
      }
    },
    onUnexpectedSessionStop: () => {
      // The channel already closed its WS to force a reconnect; the
      // service's status flips through allChannelsDisconnected().
    },
    onBackendError: () => {
      // Logged + Sentry'd inside the channel; nothing actionable
      // service-side today.
    },
    onUnknownMessage: () => {
      // Logged + Sentry'd inside the channel.
    },
  }

  /** Per-session Sentry-capture counter for unexpected mic track endings. Reset
   *  on start(). See TRACK_ENDED_CAPTURE_LIMIT. */
  private micTrackEndedCaptureCount = 0
  /** Per-session Sentry-capture counter for unexpected system track endings.
   *  Reset on start(). See TRACK_ENDED_CAPTURE_LIMIT. */
  private systemTrackEndedCaptureCount = 0

  setAudioSessionId(id: string | null, options?: { required?: boolean }): void {
    this.audioSessionId = id
    this.audioSessionRequired = options?.required === true
  }

  getStatus(): ServiceStatus {
    return this.status
  }

  /**
   * Snapshot of internal state for the "Copy Debug Info" support flow.
   * Safe to call at any time — fields are null/zero when capture isn't
   * running. The shape matches `ElectronAudioRuntimeDebugInfo` (shared
   * with the main process) so the IPC handler can return this directly.
   */
  getDebugInfo(): ElectronAudioRuntimeDebugInfo {
    return {
      status: this.status,
      audioSessionId: this.audioSessionId,
      sessionStartTime: this.sessionStartTime,
      isPaused: this.status === 'paused',
      micChannel: snapshotChannel(this.micChannel),
      systemChannel: snapshotChannel(this.systemChannel),
    }
  }

  private setStatus(status: ServiceStatus): void {
    this.status = status
    GlobalState.set('audio-transcription-service.status', status)
  }

  private setCaptureError(captureError: CaptureErrorPayload | null): void {
    GlobalState.set<CaptureErrorPayload | null>('audio-transcription-service.captureError', captureError)
  }

  private setMicCaptureError(message: string | null): void {
    this.micCaptureErrorMessage = message
    this.publishCaptureError()
  }

  private setSystemCaptureError(message: string | null): void {
    this.systemCaptureErrorMessage = message
    this.publishCaptureError()
  }

  private clearCaptureErrors(): void {
    this.micCaptureErrorMessage = null
    this.systemCaptureErrorMessage = null
    this.setCaptureError(null)
  }

  /**
   * Re-build the public `CaptureErrorPayload` from the per-channel state.
   * When both channels have errors the toast title surfaces the mic channel
   * (the more user-actionable failure, typically an OS permission dialog)
   * and the message concatenates both reasons so support sees the full
   * picture — same convention as `start()`'s original dual-failure handling.
   */
  private publishCaptureError(): void {
    const mic = this.micCaptureErrorMessage
    const system = this.systemCaptureErrorMessage
    if (mic !== null && system !== null) {
      this.setCaptureError({ channel: 'mic', error: `Mic: ${mic}. System: ${system}` })
    } else if (mic !== null) {
      this.setCaptureError({ channel: 'mic', error: mic })
    } else if (system !== null) {
      this.setCaptureError({ channel: 'system', error: system })
    } else {
      this.setCaptureError(null)
    }
  }

  /** Swallow dispose-time exceptions so the original error in a recovery
   *  path can still propagate. A diagnostics-reporter unregister race or
   *  a transport close-after-detach edge case would otherwise mask the
   *  capture failure that triggered the dispose in the first place. */
  private safeDisposeMic(): void {
    try {
      this.disposeMicChannel()
    } catch (err) {
      console.warn('[AudioTranscriptionService] disposeMicChannel threw during teardown:', err)
    }
  }

  private safeDisposeSystem(): void {
    try {
      this.disposeSystemChannel()
    } catch (err) {
      console.warn('[AudioTranscriptionService] disposeSystemChannel threw during teardown:', err)
    }
  }

  async start(options: { mic?: boolean; system?: boolean; locale?: string; micDeviceId?: string }): Promise<void> {
    if (
      this.status === 'running' ||
      this.status === 'starting' ||
      this.status === 'reconnecting' ||
      this.status === 'stopping'
    ) {
      // 'stopping' is an in-flight async teardown: stop() has begun
      // awaiting in-flight captures but has NOT yet run
      // detachMicGateListener / final cleanup. If we let start() through
      // here, attachMicGateListener no-ops (listener still attached),
      // status flips to 'running', and stop()'s tail then yanks the
      // listener out from under the fresh capture — leaving the new mic
      // session with no mute gate. Callers must wait for stop() to
      // settle before starting again.
      console.warn(`[AudioTranscriptionService] Already in ${this.status}; refusing start`)
      return
    }

    // If a `stop()` is still draining (status='stopping'), wait it
    // out before staking out the slots. Without this, start() could
    // race past stop's drainPendingRestarts await and have its
    // freshly-created channels disposed when stop() resumes.
    if (this.stopInFlight) {
      await this.stopInFlight.catch(() => {})
    }
    // Re-check status across the await — a second concurrent start()
    // may have won the race. `getStatus()` is used (not `this.status`)
    // to break TS narrowing from the early-return above; the field's
    // value can have changed across the await.
    const statusAfterStopDrain = this.getStatus()
    if (
      statusAfterStopDrain === 'running' ||
      statusAfterStopDrain === 'starting' ||
      statusAfterStopDrain === 'reconnecting' ||
      statusAfterStopDrain === 'stopping'
    ) {
      // 'stopping' here means a fresh stop() was initiated between the
      // stopInFlight await resolving (which cleared the prior teardown)
      // and this re-check landing. Falling through to setStatus('starting')
      // while another teardown is mid-flight would orphan channels.
      console.warn('[AudioTranscriptionService] Concurrent start observed during stop drain — yielding to it')
      return
    }

    // If a stop() left an in-flight restart still draining (e.g. user
    // hit start immediately after stop, before the post-stop drain
    // settled), wait for it to finish before standing up fresh
    // channels. Without this, the restart's `await startXChannel()`
    // could complete and assign a freshly-disposed-by-stop channel to
    // a slot that we just zeroed for the new start, leaving the new
    // start's channel orphaned.
    await this.drainPendingRestarts()

    // Second re-check across the drainPendingRestarts await: a
    // concurrent start() can clear the same two earlier guards (both
    // observe 'idle'), both reach this await, then one resumes,
    // flips status to 'starting', and creates channels — and the
    // second resumes against a now-non-'idle' state. Without this
    // check the second start would create a parallel set of
    // AudioChannels with live MediaStreams + WebSockets that nothing
    // ever disposes.
    const statusAfterDrain = this.getStatus()
    if (
      statusAfterDrain === 'running' ||
      statusAfterDrain === 'starting' ||
      statusAfterDrain === 'reconnecting' ||
      statusAfterDrain === 'stopping'
    ) {
      // Same 'stopping' rationale as the first re-check: drainPendingRestarts
      // can resolve concurrently with a fresh stop() starting, and yielding
      // here keeps the orphaned-channel race closed across both awaits.
      console.warn('[AudioTranscriptionService] Concurrent start observed during restart drain — yielding to it')
      return
    }

    // Refuse a session-required start with no session id before any capture
    // is stood up: the WS builder would otherwise route this meeting's audio
    // to the stateless dictation endpoint. Fail loudly; the main process is
    // expected to degrade to local transcription instead of reaching here.
    if (this.audioSessionRequired && !this.audioSessionId) {
      throw new Error(
        '[AudioTranscriptionService] Meeting recording requires an audioSessionId; refusing to start against the dictation endpoint',
      )
    }

    this.setStatus('starting')
    this.clearCaptureErrors()
    this.locale = options.locale || 'en-US'
    this.transcripts.reset()
    this.sessionStartTime = Date.now()
    // Subscribe to the atomic meeting mic-drop gate. Self-mute and merged-audio
    // arrive as one value, so there's no listener-order contract to uphold —
    // the gate can never observe `muted=true` against a stale `merged=false`.
    this.attachMicGateListener()
    // Invariant: callers must invoke setAudioSessionId() before start();
    // an unset value selects the dictation `/transcribe-ws` endpoint.
    console.log(`[AudioTranscriptionService] start: audioSessionId=${this.audioSessionId ?? 'none'}`)
    this.speakerNameResolver.reset()
    this.systemCaptureRequested = options.system === true
    this.micTrackEndedCaptureCount = 0
    this.systemTrackEndedCaptureCount = 0
    // `startMicChannel(deviceId)` (called below if `options.mic !== false`)
    // sets `this.micDeviceId` itself, so we don't need to set it here.
    // When mic isn't requested no restartMicCapture path ever runs, so
    // the field's value is unused.

    // Capture startup is parallelized; each branch stashes its own
    // failure reason locally. We surface them through `setCaptureError`
    // AFTER both branches resolve, so a slow-resolving mic error can't
    // clobber a fast-resolving system error (and vice versa) — without
    // this, a user denied both mic and screen-capture permissions
    // would see only whichever branch resolved last in GlobalState.
    const captureTasks: Array<Promise<void>> = []
    let micError: string | null = null
    let systemError: string | null = null
    let systemPermissionDenied = false
    if (options.system) {
      captureTasks.push(
        (async () => {
          try {
            await this.startSystemChannel()
          } catch (err) {
            systemError = String(err)
            systemPermissionDenied = isPermissionDeniedError(err)
            console.error('[AudioTranscriptionService] System capture failed:', err)
            this.captureCaptureFailure('system', 'initial', err, this.systemChannel?.getPrimaryTrack())
          }
        })(),
      )
    }
    if (options.mic !== false) {
      captureTasks.push(
        (async () => {
          try {
            await this.startMicChannel(options.micDeviceId)
          } catch (err) {
            micError = String(err)
            console.error('[AudioTranscriptionService] Mic capture failed:', err)
            this.captureCaptureFailure('mic', 'initial', err, this.micChannel?.getPrimaryTrack())
          }
        })(),
      )
    }
    await Promise.all(captureTasks)

    // Set per-channel errors so `publishCaptureError` builds the right
    // merged payload. The per-channel state also outlives this call —
    // if a restart later clears one channel's error, the other side
    // is preserved.
    if (micError !== null) this.setMicCaptureError(micError)
    if (systemError !== null) this.setSystemCaptureError(systemError)

    // Capture what we can: one channel failing must not take down the others,
    // so a missing system channel degrades the session to mic-only (with
    // background retries below) and vice versa. Only a start with no live
    // channel at all fails.
    const missingMic = options.mic !== false && !(this.micChannel?.hasLiveTrack() ?? false)
    const missingSystem = options.system === true && !(this.systemChannel?.hasLiveTrack() ?? false)
    if (!this.hasAnyLiveCaptureChannel()) {
      this.safeDisposeMic()
      this.safeDisposeSystem()
      this.stopEnergyReporting()
      this.diagnosticsReporter.stop()
      // Mirror runStop()'s full cleanup, otherwise the mic-gate listener
      // attached at the top of start() outlives the failed session: the
      // next start()'s attach call no-ops (guard sees the listener still
      // attached), so a fresh gate value won't be picked up until the next
      // successful stop()/start() cycle clears it.
      this.detachMicGateListener()
      this.setStatus('idle')
      GlobalState.set('asr.isRunning', false)
      const missingChannels = [missingMic ? 'microphone' : null, missingSystem ? 'system audio' : null]
        .filter((channel): channel is string => channel !== null)
        .join(' and ')
      throw new Error(`Failed to start audio transcription service: ${missingChannels} capture is unavailable`)
    }

    this.startEnergyReporting()
    this.diagnosticsReporter.start()
    this.setStatus('running')
    if (this.audioSessionRequired) void this.startSelfVoice()

    // Initial getDisplayMedia failures don't fire track.onended (no track was
    // ever created), so the standard recovery path won't run. The failure is
    // usually transient contention on the screen-capture source (a sibling
    // capture session that hasn't released yet), so retry in the background.
    // Permission denials are not transient and the pre-start permission gate
    // owns them; those stay mic-only with the error surfaced.
    if (missingSystem && !systemPermissionDenied) {
      this.scheduleInitialSystemCaptureRetries()
    }
  }

  /**
   * Subscribe to the `meeting-mic-gate` GlobalState key written by
   * `MeetingDetectionService`. The gate carries self-mute + merged-audio + the
   * precomputed `shouldDropMic` decision atomically; only an explicit
   * `shouldDropMic === true` zeroes the mic. This decoupling is deliberate:
   * per-tile `isSelf` detection in Meet/Zoom can flake (Meet hides the "your"
   * button after some UI transitions, Zoom never carries (me) on tiles), so the
   * gate must NOT depend on it.
   */
  private attachMicGateListener(): void {
    if (this.micGateListener) return
    // Initial synchronous read may return the default if the renderer-side
    // GlobalState cache hasn't received the first applyStateToStore yet
    // (renderer mounted very quickly before the main → renderer state snapshot
    // landed). We register the listener immediately so we don't miss any
    // updates during the warm-up window, then re-sync once `whenReady()`
    // resolves — see `resyncMicGateAfterReady`.
    this.micGate = this.readMicGate()
    const listener = (value: unknown): void => {
      const next = normalizeMeetingMicGate(value)
      if (meetingMicGatesEqual(next, this.micGate)) return
      this.micGate = next
      console.log(`[AudioTranscriptionService] meeting mic gate → ${describeMicGate(next)}`)
    }
    this.micGateListener = listener
    GlobalState.on(MEETING_MIC_GATE_KEY, listener)
    void this.resyncMicGateAfterReady(listener)
  }

  /**
   * After the first main→renderer state snapshot applies, re-read the gate once
   * in case it had landed in the cache between this service's initial read and
   * the listener registering (a bulk snapshot apply may not emit a per-key
   * change event). Kept as a named async method rather than an inline
   * `void whenReady().then(...)` so the fire-and-forget reads cleanly.
   */
  private async resyncMicGateAfterReady(listener: (value: unknown) => void): Promise<void> {
    await GlobalState.whenReady()
    // Bail if stop() swapped/cleared the listener during the await — we no
    // longer own it (or are owned by a different invocation).
    if (this.micGateListener !== listener) return
    listener(this.readMicGate())
  }

  private readMicGate(): MeetingMicGate {
    return normalizeMeetingMicGate(GlobalState.get<MeetingMicGate>(MEETING_MIC_GATE_KEY, NEUTRAL_MEETING_MIC_GATE))
  }

  private detachMicGateListener(): void {
    if (!this.micGateListener) return
    GlobalState.off(MEETING_MIC_GATE_KEY, this.micGateListener)
    this.micGateListener = null
    this.micGate = NEUTRAL_MEETING_MIC_GATE
  }

  private hasAnyLiveCaptureChannel(): boolean {
    return (this.micChannel?.hasLiveTrack() ?? false) || (this.systemChannel?.hasLiveTrack() ?? false)
  }

  pause(): void {
    if (this.status !== 'running') return
    // Pause both channels — drops the cached per-frame timestamps so
    // the post-resume first frame is treated as a delta-less "first
    // frame", and pauses the read loop's frame dispatch (frames are
    // still drained from the processor to avoid backpressure).
    // Wrap each channel call: a pipeline.clearFrameTimings / capture.pause
    // throw on one channel must not stop us pausing the other or
    // leaving the service stuck in a status mismatch.
    try {
      this.micChannel?.pause()
    } catch (err) {
      console.error('[AudioTranscriptionService] mic.pause() threw:', err)
      this.setMicCaptureError(`Pause failed: ${String(err)}`)
    }
    try {
      this.systemChannel?.pause()
    } catch (err) {
      console.error('[AudioTranscriptionService] system.pause() threw:', err)
      this.setSystemCaptureError(`Pause failed: ${String(err)}`)
    }
    this.setStatus('paused')
    console.log('[AudioTranscriptionService] Paused')
  }

  resume(): void {
    if (this.status !== 'paused') return
    try {
      this.micChannel?.resume()
    } catch (err) {
      console.error('[AudioTranscriptionService] mic.resume() threw:', err)
      this.setMicCaptureError(`Resume failed: ${String(err)}`)
    }
    try {
      this.systemChannel?.resume()
    } catch (err) {
      console.error('[AudioTranscriptionService] system.resume() threw:', err)
      this.setSystemCaptureError(`Resume failed: ${String(err)}`)
    }
    this.setStatus('running')
    console.log('[AudioTranscriptionService] Resumed')

    // Channel tracks can end while we're paused (pipeline throw,
    // OS yanking a USB mic, OS putting AirPods to sleep, etc).
    // `onCaptureEnded` deliberately skips recovery while paused — we
    // don't want to reopen a paused mic to a new live stream. But on
    // resume we need to re-arm those skipped restarts, otherwise the
    // user is back to "running" status with a half-dead capture and
    // no audio meter activity until they manually stop/start.
    if (this.micChannel && !this.micChannel.hasLiveTrack()) {
      console.warn('[AudioTranscriptionService] Resumed with dead mic channel — restarting capture')
      this.lastMicEnergy = 0
      void this.restartMicCapture()
    }
    if (this.systemCaptureRequested && this.systemChannel && !this.systemChannel.hasLiveTrack()) {
      console.warn('[AudioTranscriptionService] Resumed with dead system channel — restarting capture')
      this.lastSystemEnergy = 0
      void this.restartSystemCapture()
    }
  }

  /**
   * Reset detection-related internal state without tearing down WebSocket
   * connections or audio capture. Called when the AudioRuntimeWindow is
   * kept alive across stop/start cycles so that stale energy levels,
   * text segments, and speaker evidence don't cause false detections on
   * the next start.
   */
  flushDetectionState(): void {
    this.transcripts.reset()
    this.lastMicEnergy = 0
    this.lastSystemEnergy = 0
    this.speakerNameResolver.reset()
    this.pendingForceSegments.clear()
  }

  async stop(): Promise<void> {
    // Idle / mid-stop: short-circuit. A concurrent caller during a
    // mid-stop awaits the SAME stopInFlight promise rather than
    // starting a parallel teardown that would race the disposal
    // calls below.
    if (this.status === 'idle') return
    if (this.status === 'stopping') {
      if (this.stopInFlight) await this.stopInFlight
      return
    }
    const stop = this.runStop()
    this.stopInFlight = stop
    try {
      await stop
    } finally {
      if (this.stopInFlight === stop) {
        this.stopInFlight = null
      }
    }
  }

  private async runStop(): Promise<void> {
    this.setStatus('stopping')
    this.systemCaptureRequested = false
    this.initialSystemRetryAbort?.abort()
    this.initialSystemRetryAbort = null
    // Wait for any in-flight capture restart/retry to settle before
    // tearing down channels — otherwise the awaited start could
    // resolve after teardown and leave an orphaned MediaStream.
    // startSystem/MicChannel's own `this.systemChannel !== channel`
    // guard then drops the late stream cleanly.
    await this.drainPendingRestarts()
    this.lastAudioOutputSnapshot = null
    this.detachMicGateListener()
    this.clearCaptureErrors()

    this.stopEnergyReporting()
    this.diagnosticsReporter.stop()

    // Resolve any in-flight forceSegment awaiters before tearing down
    // channels so awaiters don't hang for the full timeout when the
    // service stops.
    for (const source of Array.from(this.pendingForceSegments.keys())) {
      this.resolvePendingForceSegments(source)
    }

    // Use safe wrappers so a dispose-time throw on one channel still
    // lets us tear down the other and reach the `idle` status flip —
    // otherwise the service is wedged in `stopping` forever and the
    // next start refuses to run.
    this.finishSelfVoice()
    this.safeDisposeMic()
    this.safeDisposeSystem()

    this.setStatus('idle')
    this.audioSessionId = null
    this.audioSessionRequired = false
  }

  /** Starts self-voice matching for a meeting recording when main says it's on. Never blocks capture. */
  private async startSelfVoice(): Promise<void> {
    try {
      const config = await GlobalEvents.invoke<SelfVoiceSessionConfig>(SELF_VOICE_SESSION_CONFIG_EVENT)
      if (config.mode === 'off') {
        console.log('[SelfVoice] Off for this meeting')
        return
      }
      if (this.selfVoice) return
      if (this.status !== 'running' && this.status !== 'reconnecting') return
      this.selfVoice = new SelfVoiceSession({
        matching: config.matching,
        scoreEmbedding: scoreAgainstFingerprint,
        loadEmbedder: loadVoiceEmbedder,
        ...(config.mode === 'consolidate' ? { sendVerdict: (verdict) => this.sendSelfVoiceVerdict(verdict) } : {}),
        onFirstMatch: (match) => {
          console.log(`[SelfVoice] First match: mic epoch ${match.micEpoch}, user is ${match.speakerKey}`)
          GlobalEvents.invoke(SELF_VOICE_FIRST_MATCH_EVENT, match).catch((error) =>
            console.error('[SelfVoice] Could not report the first match to main', error),
          )
        },
        isMergedAudio: () => this.micGate.mergedAudio,
      })
      console.log(`[SelfVoice] On in ${config.mode} mode; ${config.matching ? 'matching' : 'not matching'}`)
    } catch (error) {
      console.error('[SelfVoice] Could not start; this meeting goes without it', error)
    }
  }

  /** Sends a verdict to the audio session and, once it's there, relabels the desktop transcript to match. */
  private sendSelfVoiceVerdict(verdict: MicEpochVerdict): boolean {
    if (!this.micChannel?.sendSelfVoice(verdict)) return false
    console.log(`[SelfVoice] Mic epoch ${verdict.micEpoch}: user is ${verdict.speakerKey ?? 'no voice'}`)
    const matches = { ...GlobalState.get<UserVoiceMatches>(USER_VOICE_MATCHES_STATE, {}) }
    if (verdict.speakerKey) matches[verdict.micEpoch] = verdict.speakerKey
    else delete matches[verdict.micEpoch]
    void GlobalState.setIfChangedInMain(USER_VOICE_MATCHES_STATE, matches).catch((error) =>
      console.error('[SelfVoice] Could not publish the voice match', error),
    )
    return true
  }

  private finishSelfVoice(): void {
    const session = this.selfVoice
    this.selfVoice = null
    if (!session) return
    GlobalEvents.invoke(SELF_VOICE_SESSION_END_EVENT, session.finish()).catch((error) =>
      console.error('[SelfVoice] Could not report the meeting to main', error),
    )
  }

  /**
   * Force the diagnostics reporter to emit one tick immediately,
   * bypassing the `DIAG_INTERVAL_MS` cadence. Wired up to the audio-
   * debug overlay enable hook so the channel cards populate within a
   * frame of the user opening the overlay instead of staring at
   * "channel inactive" until the next scheduled tick. Safe to call
   * even when the reporter isn't running (no providers → no-op).
   */
  forceDiagnosticTick(): void {
    this.diagnosticsReporter.forceTickNow()
  }

  getTextForRange(source: number, startTime: number, endTime: number): string {
    return this.transcripts.getTextForRange(source, startTime, endTime)
  }

  /**
   * Request a segment flush and resolve once the corresponding final
   * transcript arrives (or after FORCE_SEGMENT_TIMEOUT_MS if the server
   * never responds).
   *
   * For source `2` (BOTH), waits for finals on whichever channels are
   * open. Returns immediately if no live channel is available.
   */
  forceSegment(source: number): Promise<void> {
    const now = Date.now()
    const wantMic = source === SOURCE_MICROPHONE || source === SOURCE_BOTH
    const wantSystem = source === SOURCE_LOOPBACK || source === SOURCE_BOTH

    const waitChannels: Array<number> = []

    if (wantMic && this.micChannel?.forceSegment(now)) {
      waitChannels.push(SOURCE_MICROPHONE)
    }
    if (wantSystem && this.systemChannel?.forceSegment(now)) {
      waitChannels.push(SOURCE_LOOPBACK)
    }

    if (waitChannels.length === 0) {
      return Promise.resolve()
    }

    return Promise.all(waitChannels.map((s) => this.waitForFinalSegment(s))).then(() => undefined)
  }

  /**
   * Snapshot the debug-tap stages for one channel as a list of
   * self-contained WAV files:
   *
   *   - `raw.wav` (when the source runs off the target rate) is the
   *     pre-resample mono downmix at the device's native rate.
   *   - `final.wav` is byte-identical to what is shipped over the
   *     WebSocket to Deepgram (PCM16 mono at TARGET_SAMPLE_RATE).
   *
   * Returns `null` when the channel doesn't exist. The array is empty
   * until the channel has processed a frame with audio-debug mode
   * enabled — the debug ring buffers only exist while it's on.
   */
  dumpChannelAudioBundle(channel: ChannelLabel): Array<DebugCaptureStage> | null {
    const ch = channel === 'mic' ? this.micChannel : this.systemChannel
    if (!ch) return null
    return ch.dumpDebugBundle()
  }

  /** Audio duration currently held in a channel's debug ring
   *  buffer. Returns 0 when the channel isn't active or has not
   *  produced any frames yet. */
  getRecordedSeconds(channel: ChannelLabel): number {
    const ch = channel === 'mic' ? this.micChannel : this.systemChannel
    return ch?.getRecordedSeconds() ?? 0
  }

  /** Drop one channel's debug buffer. Use right before reproducing
   *  a specific failure so the next dump captures only the audio
   *  that triggered it. */
  clearAudioDebugBuffer(channel: ChannelLabel): void {
    const ch = channel === 'mic' ? this.micChannel : this.systemChannel
    ch?.clearDebugBuffer()
  }

  /** Free every channel's debug ring buffers. Called when audio-debug
   *  mode turns off so the multi-MB buffers don't outlive the
   *  debugging session; they re-allocate lazily on the next enable. */
  releaseAudioDebugBuffers(): void {
    this.micChannel?.releaseDebugBuffers()
    this.systemChannel?.releaseDebugBuffers()
  }

  private waitForFinalSegment(source: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const pending: PendingForceSegment = { resolve, timeoutHandle: 0 as unknown as ReturnType<typeof setTimeout> }
      pending.timeoutHandle = setTimeout(() => {
        this.removePendingForceSegment(source, pending)
        console.warn(
          `[AudioTranscriptionService] forceSegment(${source}) timed out after ${FORCE_SEGMENT_TIMEOUT_MS}ms`,
        )
        resolve()
      }, FORCE_SEGMENT_TIMEOUT_MS)

      const list = this.pendingForceSegments.get(source) ?? []
      list.push(pending)
      this.pendingForceSegments.set(source, list)
    })
  }

  private removePendingForceSegment(source: number, pending: PendingForceSegment): void {
    const list = this.pendingForceSegments.get(source)
    if (!list) return
    const idx = list.indexOf(pending)
    if (idx >= 0) list.splice(idx, 1)
    if (list.length === 0) this.pendingForceSegments.delete(source)
  }

  private resolvePendingForceSegments(source: number): void {
    const list = this.pendingForceSegments.get(source)
    if (!list || list.length === 0) return
    this.pendingForceSegments.delete(source)
    for (const pending of list) {
      clearTimeout(pending.timeoutHandle)
      pending.resolve()
    }
  }

  resetSpeakerEvidence(): void {
    this.speakerNameResolver.reset()
  }

  getEnergy(source: number): number {
    if (source === SOURCE_MICROPHONE) return this.lastMicEnergy
    if (source === SOURCE_LOOPBACK) return this.lastSystemEnergy
    return Math.max(this.lastMicEnergy, this.lastSystemEnergy)
  }

  // ── Channel lifecycle ─────────────────────────────────────────

  private allChannelsDisconnected(): boolean {
    const channels = [this.micChannel, this.systemChannel].filter((c): c is AudioChannel => c !== null)
    return channels.length > 0 && channels.every((c) => !c.isWsOpen())
  }

  /** Build the WS URL for the given channel. Reads the current
   *  audioSessionId / locale on every call so that mid-session
   *  changes (none today, but cheap) take effect on the next
   *  reconnect. */
  private buildWsUrl(channel: ChannelLabel): string {
    const baseUrl = getChatApiUrl()
    const wsProto = baseUrl.startsWith('https') ? 'wss' : 'ws'
    const wsHost = baseUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '')

    // No audioSessionId → dictation path (stateless /transcribe-ws).
    // The worker reads `language` from URL query params at connection-
    // creation time and bakes it into the Deepgram client before any
    // client message arrives, so language MUST be on the URL —
    // sending it in the config body is a no-op.
    if (!this.audioSessionId) {
      // start() already refuses this, but a reconnect can race a session
      // reset; a session-required meeting must never build the dictation URL.
      // Channel connect paths catch and surface this.
      if (this.audioSessionRequired) {
        throw new Error(
          '[AudioTranscriptionService] Meeting recording requires an audioSessionId; refusing to build the dictation /transcribe-ws URL',
        )
      }
      return `${wsProto}://${wsHost}/api/v1/transcribe-ws?${new URLSearchParams({ language: this.locale }).toString()}`
    }
    // IMPORTANT: the URL `channel` param must be `'mic' | 'system'` (the
    // server-side type), NOT the internal `ChannelLabel` which spells the
    // loopback channel as `'sys'`. Sending `'sys'` here regresses to no
    // `deepgram-connected` status arriving from the audio-sessions DO and
    // the channel buffers indefinitely until overflow.

    // Lets the DO tell a same-device reconnect (rebind) from a device switch (re-dial).
    const micDeviceKey = channel === 'mic' ? micDeviceKeyFor(this.micDeviceId) : undefined

    return `${wsProto}://${wsHost}/api/v1/audio-sessions/${this.audioSessionId}/ws?${new URLSearchParams({
      language: this.locale,
      channel: wireChannelName(channel),
      diarize: 'true',
      ...(micDeviceKey ? { micDeviceKey } : {}),
    }).toString()}`
  }

  private disposeMicChannel(): void {
    if (!this.micChannel) return
    const ch = this.micChannel
    this.micChannel = null
    // Clear the resolved-device tracking so `switchMicDevice`'s
    // "in-flight already on the requested device" shortcut can't
    // match against a stale value from a now-disposed channel.
    this.activeMicDeviceId = undefined
    this.diagnosticsReporter.removeProvider(ch.snapshot)
    ch.dispose()
    // Drop the per-channel debug-overlay state. No-op when the
    // master toggle is off; keeps the overlay from showing a
    // greyed-out card after the channel is gone.
    clearAudioDebugChannelState('mic')
  }

  private disposeSystemChannel(): void {
    if (!this.systemChannel) return
    const ch = this.systemChannel
    this.systemChannel = null
    this.diagnosticsReporter.removeProvider(ch.snapshot)
    ch.dispose()
    clearAudioDebugChannelState('sys')
  }

  private async startMicChannel(deviceId?: string): Promise<void> {
    console.log('[AudioTranscriptionService] startMicCapture: starting (deviceId=%s)', deviceId)
    this.disposeMicChannel()
    this.micDeviceId = deviceId
    const capture = new MicCaptureSource(deviceId)
    const channel = this.createChannel('mic', capture)
    // Assign before awaiting capture acquisition so a concurrent
    // stop() can observe and tear it down. See spawnChannelStart for
    // the full race rationale.
    this.micChannel = channel
    this.diagnosticsReporter.addProvider(channel.snapshot)
    await this.spawnChannelStart(
      channel,
      () => this.micChannel === channel,
      () => {
        if (this.micChannel === channel) this.micChannel = null
      },
    )
    // Record the actually-opened device only if the channel survived
    // the await (a concurrent stop / restart may have nulled it). Used
    // by `switchMicDevice` to detect whether an in-flight restart has
    // already re-acquired on the requested device and skip a redundant
    // follow-up restart.
    if (this.micChannel === channel) {
      this.activeMicDeviceId = deviceId
      if (this.audioSessionId) reportMicKindToMain(this.audioSessionId, micKindForLabel(capture.deviceLabel))
    }
  }

  private async startSystemChannel(): Promise<void> {
    this.disposeSystemChannel()
    const channel = this.createChannel('sys', new SystemCaptureSource())
    this.systemChannel = channel
    this.diagnosticsReporter.addProvider(channel.snapshot)
    await this.spawnChannelStart(
      channel,
      () => this.systemChannel === channel,
      () => {
        if (this.systemChannel === channel) this.systemChannel = null
      },
    )
    if (this.systemChannel === channel) {
      // Re-baseline the audiooutput fingerprint now that the new tap
      // is bound to the current default output. Clear the existing
      // baseline synchronously BEFORE kicking off the (async) snapshot
      // so that any `devicechange` arriving during the snapshot
      // computation sees `null` and re-baselines via the
      // `handleAudioOutputDeviceChange` null branch — instead of
      // diffing against the stale pre-restart fingerprint and
      // spuriously triggering another system-capture restart.
      this.lastAudioOutputSnapshot = null
      void this.refreshAudioOutputSnapshot()
    }
  }

  /** Construct an AudioChannel without starting it — exists so the
   *  service can assign the channel to its slot BEFORE the (async)
   *  start, giving concurrent `stop()` a fully-formed object to
   *  dispose without racing with capture acquisition. */
  private createChannel(label: ChannelLabel, capture: AudioCaptureSource): AudioChannel {
    const isHost = label === 'mic'
    const source = isHost ? SOURCE_MICROPHONE : SOURCE_LOOPBACK
    const channel = new AudioChannel(
      { source, isHost, label, sessionStartTime: this.sessionStartTime },
      capture,
      this.channelOwner,
      this.channelDelegate,
    )
    if (this.status === 'paused') {
      // Restarts can happen while paused (e.g. system track ended or
      // output device changed). Preserve the service-level pause so
      // the replacement channel drains but does not process/send PCM
      // until resume().
      channel.pause()
    }
    return channel
  }

  /**
   * Start an already-assigned channel and handle three concurrent
   * teardown shapes:
   *   1. `channel.start()` throws — propagate the error so the start
   *      path can surface it to GlobalState.
   *   2. `channel.start()` succeeds but `stop()` ran during the
   *      capture acquisition window — the slot was nulled by
   *      `disposeXChannel`, so we dispose the in-flight channel
   *      ourselves to avoid leaking a live MediaStream + WS.
   *   3. `channel.start()` succeeds and we still own the slot —
   *      normal path; nothing more to do here.
   */
  private async spawnChannelStart(
    channel: AudioChannel,
    isStillOwned: () => boolean,
    clearSlot: () => void,
  ): Promise<void> {
    try {
      await channel.start()
    } catch (err) {
      // capture acquisition failed; clean up our slot + diagnostics
      if (isStillOwned()) clearSlot()
      this.diagnosticsReporter.removeProvider(channel.snapshot)
      channel.dispose()
      throw err
    }
    if (!isStillOwned()) {
      // Concurrent stop / restart replaced or cleared our slot
      // during the await. Dispose silently — diagnostics provider
      // was already removed by the disposeXChannel call.
      channel.dispose()
    }
  }

  // ── System loopback recovery ──────────────────────────────────

  /**
   * Re-baseline `lastAudioOutputSnapshot` after a successful system
   * capture start (or restart) so the next devicechange has something
   * to diff against. This is the ONLY place that sets the snapshot
   * after the initial baseline — handleAudioOutputDeviceChange used
   * to also set it before kicking off a restart, but that was racy
   * when restartSystemCapture deduped against an already-in-flight
   * retry (we'd update the baseline to a value the in-flight restart
   * wasn't actually targeting). Routing every snapshot update
   * through here keeps the baseline aligned with the tap that's
   * actually bound.
   */
  private async refreshAudioOutputSnapshot(): Promise<void> {
    this.lastAudioOutputSnapshot = await snapshotAudioOutputs()
  }

  /**
   * Set the channel-restart in-flight ref to the given promise and
   * arrange for the ref to clear when the promise settles.
   * Centralises the assign-then-finalize pattern that the system
   * restart, system retry, and mic restart paths all use; keeps the
   * "is a restart in flight?" semantics consistent across them.
   */
  private trackRestart(slot: 'mic' | 'system', restart: Promise<void>): Promise<void> {
    if (slot === 'mic') {
      this.micRestartInFlight = restart
      return restart.finally(() => {
        if (this.micRestartInFlight === restart) {
          this.micRestartInFlight = null
        }
      })
    }
    this.systemRestartInFlight = restart
    return restart.finally(() => {
      if (this.systemRestartInFlight === restart) {
        this.systemRestartInFlight = null
      }
    })
  }

  /**
   * Wait for any in-flight mic/system restart to settle, then null
   * the slots. Called from both `start()` (so a fresh start doesn't
   * race a stop-leftover restart) and `stop()` (so teardown doesn't
   * leave an orphaned MediaStream behind a still-resolving start).
   */
  private async drainPendingRestarts(): Promise<void> {
    const inFlightSystem = this.systemRestartInFlight
    const inFlightMic = this.micRestartInFlight
    this.systemRestartInFlight = null
    this.micRestartInFlight = null
    if (inFlightSystem) await inFlightSystem.catch(() => {})
    if (inFlightMic) await inFlightMic.catch(() => {})
  }

  /**
   * Renderer-level `devicechange` handler. Called from
   * audio-runtime-entry.ts (debounced) whenever the OS reports an
   * audio device change.
   *
   * If the audiooutput list actually changed since our last baseline
   * AND we're actively capturing system audio, we re-create the
   * displayMedia stream so the new CATAP tap binds to the now-current
   * default output device. The canonical case is AirPods (or any
   * BT/USB output) disconnecting mid-meeting: macOS reroutes the
   * default output but the existing tap stays bound to the
   * now-defunct endpoint and goes silent — `track.onended` does not
   * fire.
   *
   * Crucially this does NOT touch:
   *   - the meeting note / audio session (audioSessionId is preserved)
   *   - the mic channel (mic transcript continues uninterrupted)
   *   - the existing transcript text segments
   * The only user-visible effect is a ~1-2s gap in system audio while
   * the new displayMedia stream + WebSocket come up. Transcripts that
   * arrive after the new WS connects flow into the same audio session
   * DO and the meeting note keeps appending.
   */
  async handleAudioOutputDeviceChange(): Promise<void> {
    if (!this.systemCaptureRequested || !this.systemChannel) return
    if (this.status === 'idle' || this.status === 'stopping' || this.status === 'starting') return
    if (this.systemRestartInFlight) return

    const current = await snapshotAudioOutputs()

    // Race with startSystemChannel's initial baseline — establish it
    // now and skip the restart so we don't churn on the very first
    // devicechange.
    if (this.lastAudioOutputSnapshot === null) {
      this.lastAudioOutputSnapshot = current
      return
    }

    if (current === this.lastAudioOutputSnapshot) return

    console.log(
      '[AudioTranscriptionService] Audio output devices changed — restarting system loopback to retarget CATAP tap',
    )
    console.log(`[AudioTranscriptionService]   before: ${this.lastAudioOutputSnapshot || '(empty)'}`)
    console.log(`[AudioTranscriptionService]   after:  ${current || '(empty)'}`)
    // NOTE: do NOT update `lastAudioOutputSnapshot` here. The
    // baseline is only valid once the new tap is actually bound to
    // the new default output, which happens at the end of
    // restartSystemCapture → startSystemChannel → refreshAudioOutputSnapshot.
    // Updating it here would lie about which device list the active
    // tap is bound to and could mask a follow-up change that comes
    // in mid-restart.
    void this.restartSystemCapture()
  }

  /**
   * Tear down the existing system capture, wait briefly for the OS to
   * release the loopback source, and acquire a fresh one. Concurrent
   * invocations share the same in-flight promise.
   */
  private restartSystemCapture(): Promise<void> {
    if (this.systemRestartInFlight) {
      return this.systemRestartInFlight
    }
    const restart = (async () => {
      this.disposeSystemChannel()
      this.lastSystemEnergy = 0
      // Brief delay to give the OS time to release the prior loopback
      // source before re-acquiring; without this, getDisplayMedia
      // sometimes returns the now-defunct tap (especially on AirPods
      // disconnect under macOS).
      await new Promise((resolve) => setTimeout(resolve, 500))
      // Bail if the service moved out of a recoverable state during
      // the wait.
      if (
        !this.systemCaptureRequested ||
        this.status === 'idle' ||
        this.status === 'stopping' ||
        this.status === 'starting'
      ) {
        return
      }
      try {
        await this.startSystemChannel()
        this.setSystemCaptureError(null)
        console.log('[AudioTranscriptionService] System capture restarted successfully')
        publishAudioDebugEvent({
          atMs: Date.now(),
          channel: 'sys',
          severity: 'info',
          message: 'system capture restarted successfully',
        })
      } catch (err) {
        console.error('[AudioTranscriptionService] System capture restart failed:', err)
        this.setSystemCaptureError(`Restart failed: ${String(err)}`)
        publishAudioDebugEvent({
          atMs: Date.now(),
          channel: 'sys',
          severity: 'error',
          message: `system capture restart failed: ${String(err)}`,
        })
      }
    })()
    return this.trackRestart('system', restart)
  }

  /**
   * Report a capture-acquire failure (mic or system) to Sentry with full
   * diagnostic context — track readyState/settings, audio device enumeration,
   * audio session id. Fire-and-forget; the catch in here exists only so a
   * broken Sentry transport (no DSN, network down) can't take down the
   * recording flow.
   *
   * `phase` distinguishes the initial `start()` acquire from the bounded
   * `scheduleInitialSystemCaptureRetries` retries so we can tell at a glance
   * which retry attempt finally gave up.
   */
  private captureCaptureFailure(
    channel: 'mic' | 'system',
    phase: 'initial' | 'retry-exhausted',
    err: unknown,
    track?: MediaStreamTrack | null,
    extra?: Record<string, unknown>,
  ): void {
    void (async () => {
      try {
        const ctx = await this.gatherAudioContext(track)
        const errObj = err instanceof Error ? err : new Error(String(err))
        Sentry.captureException(errObj, {
          tags: {
            component: 'audio-transcription-service',
            errorId: 'capture-acquire-failed',
            channel,
            phase,
          },
          extra: { ...ctx, ...(extra ?? {}) },
        })
      } catch (sentryErr) {
        // Don't let a Sentry transport failure suppress the real error or
        // prevent the calling path from continuing its recovery loop.
        console.warn('[AudioTranscriptionService] captureCaptureFailure: Sentry send failed:', sentryErr)
      }
    })()
  }

  /**
   * Report an unexpected track-ended event to Sentry with diagnostic context,
   * rate-limited per session per channel by TRACK_ENDED_CAPTURE_LIMIT. Captures
   * the first N occurrences (not just one) so that "one transient ending" vs
   * "ends N+ times — wedged" is visible from event volume in Sentry. Past the
   * cap the recovery loop still runs but Sentry events stop being sent so a
   * pathological session can't flood the project quota.
   */
  private captureTrackEnded(channel: 'mic' | 'system', track?: MediaStreamTrack | null): void {
    const count = channel === 'mic' ? this.micTrackEndedCaptureCount : this.systemTrackEndedCaptureCount
    if (count >= TRACK_ENDED_CAPTURE_LIMIT) return
    if (channel === 'mic') this.micTrackEndedCaptureCount = count + 1
    else this.systemTrackEndedCaptureCount = count + 1
    const occurrence = count + 1
    void (async () => {
      try {
        const ctx = await this.gatherAudioContext(track)
        Sentry.captureException(new Error(`${channel === 'mic' ? 'Mic' : 'System'} track ended unexpectedly`), {
          tags: {
            component: 'audio-transcription-service',
            errorId: 'track-ended-unexpected',
            channel,
          },
          extra: { ...ctx, occurrence, captureLimit: TRACK_ENDED_CAPTURE_LIMIT },
        })
      } catch (sentryErr) {
        console.warn('[AudioTranscriptionService] captureTrackEnded: Sentry send failed:', sentryErr)
      }
    })()
  }

  /**
   * Gather diagnostic context attached to Sentry events for capture failures.
   * Includes (when available) the in-question track's readyState/settings/label,
   * plus the full audio-device enumeration. Device labels only populate after
   * the user has granted mic permission to this origin — for the first-call
   * permission-denied path the labels will be empty, which is itself a useful
   * signal that gets serialized into the payload.
   *
   * Wrapped in a try/catch per field so a single failing read can't suppress
   * the rest of the diagnostic context.
   */
  private async gatherAudioContext(track?: MediaStreamTrack | null): Promise<Record<string, unknown>> {
    const ctx: Record<string, unknown> = { audioSessionId: this.audioSessionId }
    if (track) {
      try {
        ctx.trackReadyState = track.readyState
      } catch {
        // ignore
      }
      try {
        ctx.trackSettings = track.getSettings()
      } catch {
        // ignore
      }
      try {
        ctx.trackLabel = track.label
        ctx.trackId = track.id
      } catch {
        // ignore
      }
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      ctx.audioInputs = devices
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || '(unlabeled)', groupId: d.groupId }))
      ctx.audioOutputs = devices
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || '(unlabeled)', groupId: d.groupId }))
    } catch (err) {
      ctx.enumerateDevicesError = err instanceof Error ? err.message : String(err)
    }
    return ctx
  }

  /**
   * Recover the initial system channel in the background after start()
   * proceeded without it (mic-only). Transient CATAP contention usually
   * clears within seconds. The 2s/5s/12s sleeps are abortable so runStop
   * never waits out a delay; each acquire attempt runs under trackRestart so
   * stop() still drains an in-flight getDisplayMedia.
   */
  private scheduleInitialSystemCaptureRetries(): void {
    if (this.initialSystemRetryAbort) {
      return
    }
    const abort = new AbortController()
    this.initialSystemRetryAbort = abort
    const delays = [2000, 5000, 12000]

    const retryLoop = async (): Promise<void> => {
      for (let attempt = 1; attempt <= delays.length; attempt += 1) {
        await sleep(delays[attempt - 1], abort.signal)
        if (
          abort.signal.aborted ||
          !this.systemCaptureRequested ||
          this.status === 'idle' ||
          this.status === 'stopping'
        ) {
          return
        }
        // Don't retry if some other path already brought system capture up.
        if (this.systemChannel?.hasLiveTrack()) {
          this.setSystemCaptureError(null)
          return
        }
        if (this.systemRestartInFlight) {
          continue
        }
        console.warn(
          `[AudioTranscriptionService] Retrying initial system capture (attempt ${attempt}/${delays.length})`,
        )
        try {
          await this.trackRestart('system', this.startSystemChannel())
          this.setSystemCaptureError(null)
          console.log('[AudioTranscriptionService] System capture acquired on retry')
          return
        } catch (err) {
          console.error(`[AudioTranscriptionService] System capture retry ${attempt} failed:`, err)
          this.setSystemCaptureError(`Retry ${attempt} failed: ${String(err)}`)
          if (isPermissionDeniedError(err)) {
            return
          }
        }
      }
      console.warn('[AudioTranscriptionService] Initial system capture retries exhausted, recording mic-only')
      this.captureCaptureFailure(
        'system',
        'retry-exhausted',
        new Error(`Initial system capture retries exhausted after ${delays.length} attempts`),
        null,
        { attempts: delays.length },
      )
    }

    void retryLoop()
      .catch((err) => console.error('[AudioTranscriptionService] Initial system capture retry loop failed:', err))
      .finally(() => {
        if (this.initialSystemRetryAbort === abort) {
          this.initialSystemRetryAbort = null
        }
      })
  }

  // ── Mic recovery & device switching ───────────────────────────

  /**
   * Restart mic capture with a bounded retry chain. Symmetric with the
   * system path's `scheduleInitialSystemCaptureRetries`: a transient mic
   * failure (USB unplug/replug, BT mic re-pairing, OS audio service
   * blip) used to give up after a single attempt and leave the user
   * mic-less for the rest of the session. The 500ms/2s/5s backoff
   * mirrors the system retry timing while staying short enough that
   * a normal hot-plug recovers within ~7s.
   */
  private restartMicCapture(): Promise<void> {
    if (this.micRestartInFlight) return this.micRestartInFlight
    const restart = this.runMicRestart(1)
    return this.trackRestart('mic', restart)
  }

  private static readonly MIC_RESTART_DELAYS_MS = [500, 2000, 5000]

  private async runMicRestart(attempt: number): Promise<void> {
    const delays = AudioTranscriptionService.MIC_RESTART_DELAYS_MS
    const maxAttempts = delays.length
    // Dispose on the FIRST attempt only — subsequent attempts already
    // have the slot empty (the prior startMicChannel threw before
    // assigning a live track) and double-disposing is a no-op anyway.
    if (attempt === 1) {
      this.safeDisposeMic()
      // Zero the cached RMS so the broadcast energy meter doesn't stick on
      // the pre-disposal value before the new mic delivers a frame.
      this.lastMicEnergy = 0
    }
    await new Promise((resolve) => setTimeout(resolve, delays[attempt - 1]))
    if (this.status === 'idle' || this.status === 'stopping' || this.status === 'starting') return
    try {
      await this.startMicChannel(this.micDeviceId)
      this.setMicCaptureError(null)
      console.log(`[AudioTranscriptionService] Mic capture restarted successfully on attempt ${attempt}`)
    } catch (err) {
      console.error(`[AudioTranscriptionService] Mic capture restart attempt ${attempt}/${maxAttempts} failed:`, err)
      if (isPermissionDeniedError(err)) {
        this.setMicCaptureError(`Restart blocked by microphone permission: ${String(err)}`)
        return
      }
      if (attempt < maxAttempts) {
        // Mark transient so the user sees we're still trying instead
        // of a terminal "Restart failed" banner. Final-attempt failure
        // sets the terminal message just below.
        this.setMicCaptureError(`Restart attempt ${attempt} failed: ${String(err)} — retrying`)
        // Bail before scheduling another attempt if teardown started
        // during the await. `getStatus()` re-reads the live value across
        // the await boundary; TS narrowing from the earlier guard would
        // otherwise treat this comparison as unreachable.
        const liveStatus = this.getStatus()
        if (liveStatus === 'idle' || liveStatus === 'stopping' || liveStatus === 'starting') return
        await this.runMicRestart(attempt + 1)
        return
      }
      this.setMicCaptureError(`Restart failed after ${maxAttempts} attempts: ${String(err)}`)
    }
  }

  async switchMicDevice(deviceId: string | undefined): Promise<void> {
    if (this.status !== 'running' && this.status !== 'paused') return
    console.log(`[AudioTranscriptionService] Switching mic device to: ${deviceId || '(default)'}`)
    this.micDeviceId = deviceId
    if (this.status !== 'running') return
    // If a mic restart is already running, it reads `this.micDeviceId`
    // synchronously at its own `startMicChannel(this.micDeviceId)` call
    // site. If it hasn't reached that line yet it will pick up the new
    // device for free, and `activeMicDeviceId` (set when the channel
    // survives the start) will match what we want — no follow-up
    // restart needed. If the in-flight restart had already passed the
    // call site with the OLD value, `activeMicDeviceId` won't match
    // and we restart again to acquire the requested device.
    if (this.micRestartInFlight) {
      await this.micRestartInFlight.catch(() => {})
      if (this.status !== 'running') return
      // Only short-circuit when the in-flight restart actually produced
      // a live channel on the requested device. Without the liveTrack
      // check, a fully-failed retry chain (all 3 attempts threw —
      // `disposeMicChannel` cleared `activeMicDeviceId` to undefined)
      // would silently no-op `switchMicDevice(undefined)` because
      // `undefined === undefined`, dropping the user's explicit switch.
      if (this.activeMicDeviceId === deviceId && this.micChannel?.hasLiveTrack()) return
    }
    await this.restartMicCapture()
  }

  // ── Energy reporting ──────────────────────────────────────────

  private startEnergyReporting(): void {
    this.stopEnergyReporting()
    this.energyTimer = setInterval(() => {
      const tickNow = Date.now()
      const combined = Math.max(this.lastMicEnergy, this.lastSystemEnergy)
      // Publish raw system-audio RMS so main-process consumers (e.g.
      // the meeting-end veto) can tell whether the speakers are
      // currently playing audio. Raw RMS preferred over the scaled UI
      // value because the threshold the consumer cares about is the
      // silence boundary (~0.01) rather than the meter-scaled input range.
      // `at` lets consumers detect staleness if capture stops without
      // clearing.
      //
      // Publish 0 for systemLevel any time the cached value is not
      // a current measurement of live system audio. Three failure
      // modes converge here:
      //   1. Track ended unexpectedly / channel torn down / restart
      //      in flight → no fresh frames, cached RMS is stale.
      //   2. Service paused → the read loop is dropping frames so
      //      `lastSystemEnergy` freezes at the value it had at
      //      pause time. The meeting-end veto would otherwise see a
      //      loud cached value forever.
      //   3. Service reconnecting → the WS is down so even if frames
      //      are flowing the user's UI is in a degraded state and we
      //      shouldn't pretend audio is being captured normally.
      // Only `'running'` AND a live track means the cached
      // `lastSystemEnergy` actually reflects current system audio.
      const systemLive = this.systemChannel?.hasLiveTrack() ?? false
      const systemLevelLive = systemLive && this.status === 'running'
      const snapshot: AudioLevelsSnapshot = {
        input: Math.min(combined * 20, 3),
        system: systemLevelLive ? this.lastSystemEnergy : 0,
        at: tickNow,
      }
      // Direct renderer→renderer MessagePort fan-out via the broadcast
      // broker — never wakes the main JS loop. The cached snapshot is
      // served to main-process pullers via `audio-runtime:get-levels`.
      this.latestPublishedLevels = snapshot
      ensureAudioLevelsProducer().publish(snapshot)

      // Buffered audio per channel — only publish when the rounded
      // value actually changes so subscribers don't get a 10Hz event
      // stream during steady-state buffering. Wall-clock seconds is
      // the meaningful unit for UI ("Reconnecting · 23s buffered"); a
      // sub-second value would just be visual noise.
      this.publishBufferedState()
    }, ENERGY_REPORT_INTERVAL_MS)
  }

  /** Compute and (conditionally) publish the per-channel buffered
   *  duration. Called every energy tick during normal operation and
   *  once explicitly from `stopEnergyReporting()` so consumers see a
   *  clean zero on teardown. */
  private publishBufferedState(): void {
    const micBytes = this.micChannel?.getBufferedBytes() ?? 0
    const sysBytes = this.systemChannel?.getBufferedBytes() ?? 0
    const next: BufferedSecondsState = {
      mic: Math.round(micBytes / TARGET_BYTES_PER_SECOND),
      sys: Math.round(sysBytes / TARGET_BYTES_PER_SECOND),
    }
    if (next.mic === this.lastBufferedPublished.mic && next.sys === this.lastBufferedPublished.sys) {
      return
    }
    this.lastBufferedPublished = next
    GlobalState.set('audio-transcription-service.buffered', next)
  }

  private stopEnergyReporting(): void {
    if (this.energyTimer) {
      clearInterval(this.energyTimer)
      this.energyTimer = null
    }
    // Zero out the published levels so freshness-aware consumers don't
    // treat a lingering pre-stop value as current. Skip producer publish
    // if this singleton never registered one.
    const snapshot: AudioLevelsSnapshot = { input: 0, system: 0, at: Date.now() }
    this.latestPublishedLevels = snapshot
    if (audioLevelsProducer) audioLevelsProducer.publish(snapshot)

    // Same idea for the buffered-seconds state — clear so a UI pill
    // showing "buffered Xs" doesn't stick after stop().
    if (this.lastBufferedPublished.mic !== 0 || this.lastBufferedPublished.sys !== 0) {
      this.lastBufferedPublished = { ...EMPTY_BUFFERED_STATE }
      GlobalState.set('audio-transcription-service.buffered', { ...EMPTY_BUFFERED_STATE })
    }
  }

  /** Snapshot of the most recently published levels. Served to main-process
   *  pullers via the `audio-runtime:get-levels` IPC handler. */
  getLatestPublishedLevels(): AudioLevelsSnapshot {
    return this.latestPublishedLevels
  }

  // ── Device enumeration ────────────────────────────────────────

  async listInputDevices(): Promise<Array<string>> {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((d) => d.kind === 'audioinput').map((d) => d.label || `Microphone ${d.deviceId.slice(0, 8)}`)
  }
}

const audioTranscriptionService = new AudioTranscriptionService()
export default audioTranscriptionService
